/**
 * LaunchSummaryFixturePage — a dev-only harness for the Go Live "view changes" list
 * (`onboarding-3d-plan-floor-elevation-adoption`, epic `onboarding-3d-plan` feature 11).
 *
 * ## Why a fixture page rather than a real Go Live
 *
 * The list appears only after an UPDATE Go Live, which deletes and rewrites a twin's live model.
 * Reaching it for real means a write to a staging twin, which this feature's build is not permitted
 * to make. So the before/after A/B is captured here instead, as the design-system gates allow.
 *
 * ## What is on it
 *
 * The real `ModelDiffView`, over a diff the real `computeModelDiff` makes from two trees of one site
 * whose ONLY difference is the First floor's altitude: stored at 3.3 m live, launched at 3.5 m by
 * the plan. That is production PUL-CON's case (read-only survey, 2026-10-21): its saved plan carries
 * no elevation, so its next Go Live lifts the floor 0.2 m. Before this feature the list said
 * "No entity changes"; after it, "First floor — moved up 0.2 m". The frame is produced by the code
 * under change, never by a hard-coded diff.
 *
 * The trees live INSIDE the component on purpose — see the warning on `/link-repair-fixture` in
 * `App.tsx`: the route guard removes the route, not the module, and only data the bundler can drop
 * with the component stays out of the production bundle.
 *
 * TL_ADMIN-gated, non-production builds only, linked from no UI — reach it at
 * /launch-summary-fixture.
 */
import styled from 'styled-components';
import { computeModelDiff, type TransformNode } from '@repo/front-end-utils';
import { semantic, space, typeRole } from '@repo/design-tokens';
import ModelDiffView from '../Onboarding/ModelDiffView';

const Page = styled.main`
  min-height: 100vh;
  padding: ${space[6]};
  background: ${semantic.surface.app};
`;
const Card = styled.section`
  max-width: 32rem;
  padding: ${space[5]};
  background: ${semantic.surface.card};
  border: 1px solid ${semantic.border.default};
`;
const Title = styled.h1`
  margin: 0 0 ${space[4]};
  color: ${semantic.text.primary};
  font-size: ${typeRole.title3.fontSize};
`;

const LaunchSummaryFixturePage = () => {
  const SITE = 'PUL-CON';
  // Typed with `altitude` explicitly so the page also compiles against a `TransformNode` that lacks
  // it — the before frame of the A/B runs this page over `origin/staging`'s `modelDiff.ts`.
  const floor = (
    id: number,
    name: string,
    altitude: number,
  ): TransformNode & { altitude?: number } => ({
    bID: `${SITE}-floor-${id}`,
    name,
    type: { name: 'floor' },
    altitude,
    children: [],
  });
  const site = (firstFloorAltitude: number): TransformNode => ({
    bID: SITE,
    name: 'igym Congleton',
    type: { name: 'site' },
    children: [floor(1, 'Ground floor', 0), floor(2, 'First floor', firstFloorAltitude)],
  });
  const diff = computeModelDiff(site(3.3), site(3.5));

  return (
    <Page>
      <Card aria-label="Launch summary fixture">
        <Title>View changes</Title>
        <ModelDiffView diff={diff} />
      </Card>
    </Page>
  );
};

export default LaunchSummaryFixturePage;
